<script setup>
import { ref, computed } from 'vue';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import api from 'dashboard/api/aceleres/iaRodizios';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  curso: { type: Object, default: null },
  teamId: { type: [Number, String], default: null },
});
const emit = defineEmits(['close', 'saved']);

const isEdit = computed(() => !!props.curso?.id);

const form = ref({
  name: props.curso?.name || '',
  description: props.curso?.description || '',
  start_date: props.curso?.start_date || '',
  end_date: props.curso?.end_date || '',
  value: props.curso?.value ?? '',
  vagas: props.curso?.vagas ?? '',
  is_active: props.curso?.is_active ?? true,
});

const saving = ref(false);

const submit = async () => {
  if (!form.value.name.trim()) {
    useAlert('Informe o nome do curso');
    return;
  }
  saving.value = true;
  try {
    const payload = {
      ...form.value,
      value: form.value.value === '' ? null : Number(form.value.value),
      vagas: form.value.vagas === '' ? null : Number(form.value.vagas),
    };
    if (isEdit.value) {
      await api.updateCurso(props.curso.id, payload, props.teamId);
    } else {
      await api.createCurso(payload, props.teamId);
    }
    useAlert(isEdit.value ? 'Curso atualizado' : 'Curso criado');
    emit('saved');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao salvar curso');
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <Modal :show="true" :on-close="() => emit('close')" size="medium">
    <div class="p-6 flex flex-col gap-4 w-full">
      <h2 class="text-display-small text-n-slate-12 font-medium m-0">
        {{ isEdit ? 'Editar curso' : 'Novo curso' }}
      </h2>

      <div class="flex flex-col gap-1.5">
        <label class="text-body-small text-n-slate-12 font-medium">Nome do curso *</label>
        <input v-model="form.name" type="text" maxlength="200"
          class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
      </div>

      <div class="flex gap-4">
        <div class="flex flex-col gap-1.5 flex-1">
          <label class="text-body-small text-n-slate-12 font-medium">Data de início</label>
          <input v-model="form.start_date" type="date"
            class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
        </div>
        <div class="flex flex-col gap-1.5 flex-1">
          <label class="text-body-small text-n-slate-12 font-medium">Data de término</label>
          <input v-model="form.end_date" type="date"
            class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
        </div>
      </div>

      <div class="flex gap-4">
        <div class="flex flex-col gap-1.5 flex-1">
          <label class="text-body-small text-n-slate-12 font-medium">Valor (R$)</label>
          <input v-model="form.value" type="number" min="0" step="0.01"
            class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
        </div>
        <div class="flex flex-col gap-1.5 flex-1">
          <label class="text-body-small text-n-slate-12 font-medium">Quantidade de vagas</label>
          <input v-model="form.vagas" type="number" min="0"
            class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
        </div>
        <div class="flex items-end pb-2">
          <label class="flex items-center gap-2 text-body-small text-n-slate-12">
            <input v-model="form.is_active" type="checkbox" />
            Ativo
          </label>
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-body-small text-n-slate-12 font-medium">Descrição</label>
        <textarea v-model="form.description" rows="4"
          class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <Button label="Cancelar" variant="ghost" @click="emit('close')" />
        <Button :label="isEdit ? 'Salvar alterações' : 'Criar curso'"
                :is-loading="saving" @click="submit" />
      </div>
    </div>
  </Modal>
</template>
